import React, { useState } from 'react';
import type { AppEvent } from '@daddytime/events';
import { X, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';

interface EventDetailProps {
  event: AppEvent;
  onClose: () => void;
}

interface CollapsibleJsonProps {
  label: string;
  data: unknown;
  defaultOpen?: boolean;
}

function CollapsibleJson({ label, data, defaultOpen = false }: CollapsibleJsonProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  const jsonString = JSON.stringify(data, null, 2);

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="border border-gray-200 rounded-md">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 text-left bg-gray-50 hover:bg-gray-100"
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
          <span className="font-medium text-gray-700">{label}</span>
        </div>
        {isOpen && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-green-500">Copied</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>Copy</span>
              </>
            )}
          </button>
        )}
      </button>
      {isOpen && (
        <pre className="p-4 overflow-x-auto text-sm bg-gray-900 text-gray-100 rounded-b-md">
          {jsonString}
        </pre>
      )}
    </div>
  );
}

export default function EventDetail({ event, onClose }: EventDetailProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Event Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Event ID</label>
              <p className="font-mono text-sm bg-gray-50 p-2 rounded break-all">{event.event_id}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Event Type</label>
              <p className="text-sm bg-gray-50 p-2 rounded">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {event.event_type}
                </span>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Created At</label>
              <p className="text-sm bg-gray-50 p-2 rounded">{new Date(event.created_at).toLocaleString()}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Timestamp</label>
              <p className="text-sm bg-gray-50 p-2 rounded">
                {event.timestamp ? new Date(event.timestamp).toLocaleString() : '-'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">User ID</label>
              <p className="font-mono text-sm bg-gray-50 p-2 rounded break-all">{event.user_id || 'anonymous'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Session ID</label>
              <p className="font-mono text-sm bg-gray-50 p-2 rounded break-all">{event.session_id || '-'}</p>
            </div>
          </div>

          {/* Data JSON */}
          <CollapsibleJson label="Data" data={event.data} defaultOpen={true} />

          {/* Metadata JSON */}
          <CollapsibleJson label="Metadata" data={event.metadata} defaultOpen={false} />

          {/* Full Event JSON */}
          <CollapsibleJson label="Full Event (Raw JSON)" data={event} defaultOpen={false} />
        </div>
      </div>
    </div>
  );
}
